<script setup lang="ts">
import { NumericEvaluationParameterDto } from '@/api/generator';
import NumberField from '@/components/forms/NumberField/NumberField.vue';
import {
  formatRangeHintBounds,
  getFormattingOptionsFromEvaluationParameter,
  NumberDisplayOptions,
} from '@/utils/numberUtils';
import { I18nKey } from '@/utils/typings';
import { useTranslation } from 'i18next-vue';
import { computed, onBeforeUnmount, ref } from 'vue';

const { t } = useTranslation();

const props = defineProps<{
  evaluation: NumericEvaluationParameterDto;
  value?: number;
  disabled?: boolean;
  formatValue?: NumberDisplayOptions;
  // Emit `null` on blur when the field is cleared, instead of not emitting at all — for a
  // field editing an already-submitted value, an empty field means "clear it", which needs to
  // reach the caller just like any other change. Off by default: a not-yet-submitted manual
  // task input should never auto-submit just because the field was touched and left empty.
  allowClear?: boolean;
}>();

const modelValue = ref<number | undefined>(props.value);

const emits = defineEmits<{
  input: [result: number | null];
  // Fires on every blur, also when nothing was saved (e.g. an invalid value), so an editor behind
  // a pencil button can close. Not fired for the blur caused by unmounting.
  blur: [];
  cancel: [];
}>();

// The field also blurs when it is removed, e.g. because Cancel closed the editor. That blur is
// not a confirmation and must not save.
let unmounting = false;
onBeforeUnmount(() => (unmounting = true));

const format = computed<NumberDisplayOptions | undefined>(() => {
  if (props.formatValue) {
    return props.formatValue;
  }
  return getFormattingOptionsFromEvaluationParameter(props.evaluation);
});

const errorMessage = computed(() => {
  const [inputValue, minValue, maxValue] = [
    modelValue.value,
    props.evaluation.min ?? undefined,
    props.evaluation.max ?? undefined,
  ];

  if (inputValue === undefined) {
    return undefined;
  }

  if (isNaN(inputValue)) {
    const valueType = props.evaluation.definition?.valueType;

    return t('ErrorStrategyType', {
      type: t(valueType ? `QuantityValueType.${valueType}` : ('Input' as I18nKey)),
    });
  }

  // Check integer validation based on the computed format
  const effectiveValueType = format.value?.valueType;
  if (effectiveValueType === 'IntegerNumber' && !Number.isInteger(inputValue)) {
    return t('ErrorStrategyType', { type: t('QuantityValueType.IntegerNumber') });
  }

  const tooSmall = minValue !== undefined && inputValue < minValue;
  const tooBig = maxValue !== undefined && inputValue > maxValue;

  const unit = props.evaluation.evaluationUnitAbbreviation ?? '';

  if (tooBig || tooSmall) {
    const formatted = formatRangeHintBounds({ min: minValue, max: maxValue });
    return t('ValidationValueNotInRange', {
      minValue: formatted.minValue ?? '',
      maxValue: formatted.maxValue ?? '',
      unit,
    });
  }

  return undefined;
});

function submit(input: number | undefined) {
  if (unmounting) {
    return;
  }
  if ((input ?? null) === (props.value ?? null)) {
    if (props.value != null) emits('cancel');
    emits('blur');
    return;
  }
  if (input === undefined) {
    if (props.allowClear) {
      emits('input', null);
    }
  } else if (!errorMessage.value) {
    emits('input', input);
  }
  emits('blur');
}
</script>

<template>
  <NumberField
    v-model="modelValue"
    :label="$t('Value')"
    :suffix="evaluation.evaluationUnitAbbreviation"
    :disabled="disabled"
    :errorMessage="errorMessage"
    :format="format"
    class="mr-2 flex-grow-1"
    @blur="submit(modelValue)"
    @keyup.enter="$event.srcElement.blur()"
    @keydown.esc="emits('cancel')"
  />
</template>
